import { useTranslator } from "@eo-locale/react";

import { useRouter } from "next/router";

import { useEffect, useState } from "react";

import truncate from "truncate-html";

import xss from "xss";

import includes from "lodash/includes";

import { isEmpty } from "lodash";

import { useAppDispatch, useAppSelector } from "@/redux/store/hooks";

import {
  Box,
  Button,
  FiArrowRight,
  Flex,
  HTMLWrapper,
  Link,
  HorizontalScrollMenu,
  Contribution,
  VStack,
  Heading,
  Image
} from "@/storybook-components";
import {
  ehqWhiteListAttributes,
  dateWithSiteLocale,
  buildImgixUrl,
  stripAllHtml
} from "@/lib";

import Alerts from "@/components/alerts";
import useUser from "@/hooks/useUser";

import useEmbeds from "@/hooks/useEmbeds";
import { fetchPinDetails } from "@/redux/features/place-markers/thunk";

import PinIcon from "../esri-map/markers/pin-Icon";
import { addAssetPrefix } from "@/helpers/add-asset-prefix";

const PlacesBanner = () => {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const { isEmbed, isInsideIframe, isInsideIframeAndOnCurrentUrl } =
    useEmbeds();

  const { name, description, state, permalink, archivalReasonMessage } =
    useAppSelector((state) => state.place.place);
  const { translate, language } = useTranslator();
  const { isRegisteredUserLoggedIn } = useUser();

  const { visibilityMode, canContribute } = useAppSelector(
    (state) => state.project.project
  );
  const [showArchiveMessage, setShowArchiveMessage] = useState(
    state === "archived"
  );
  const [showDraftMessage, setShowDraftMessage] = useState(state === "draft");
  const [showPanelledVisibilityMessage, setshowPanelledVisibilityMessage] =
    useState(
      (visibilityMode === "Panelled" && !isRegisteredUserLoggedIn) ||
        (visibilityMode === "Panelled" && !canContribute)
    );

  const { projectId, placesId } = router.query;
  const getRouteTarget = () =>
    isEmbed && isInsideIframe && !isInsideIframeAndOnCurrentUrl
      ? "_blank"
      : "_parent";

  const {
    markers,
    meta: { count = 0 }
  } = useAppSelector((state) => state.placeMarkersSlice);

  useEffect(() => {
    projectId &&
      placesId &&
      dispatch(
        fetchPinDetails({
          projectId: `${projectId}`,
          placesId: `${placesId}`,
          pagination: {
            page: 1,
            page_size: 5
          },
          filters: {
            sort: { created_at: "desc" }
          }
        })
      );
  }, []);

  const statusMessages = () => {
    return (
      (showDraftMessage ||
        showArchiveMessage ||
        showPanelledVisibilityMessage) && (
        <VStack>
          {showDraftMessage && (
            <Alerts
              status="info"
              description={translate("brainstormers.thisToolIsInDraft")}
              onClose={() => setShowDraftMessage(false)}
            />
          )}
          {showArchiveMessage && (
            <Alerts
              status="info"
              description={
                archivalReasonMessage ||
                translate("brainstormers.boardArchived")
              }
              onClose={() => setShowArchiveMessage(false)}
            />
          )}
          {showPanelledVisibilityMessage && (
            <Alerts
              status="info"
              description={translate("brainstormers.visibilityPanelled")}
              onClose={() => setshowPanelledVisibilityMessage(false)}
            />
          )}
        </VStack>
      )
    );
  };
  const wrapperComponent = () => {
    return (
      <HTMLWrapper
        my={4}
        html={truncate(
          xss(description, {
            whiteList: {
              p: ehqWhiteListAttributes,
              s: ehqWhiteListAttributes,
              u: ehqWhiteListAttributes,
              em: ehqWhiteListAttributes,
              ul: ehqWhiteListAttributes,
              li: ehqWhiteListAttributes,
              ol: ehqWhiteListAttributes,
              br: ehqWhiteListAttributes,
              strong: ehqWhiteListAttributes
            },
            stripIgnoreTag: true,
            stripIgnoreTagBody: ["table", "header", "img", "video"],
            onTag: (tag) => {
              if (
                includes(
                  [
                    "img",
                    "area",
                    "base",
                    "col",
                    "embed",
                    "hr",
                    "input",
                    "link",
                    "meta",
                    "param",
                    "source",
                    "track",
                    "wbr"
                  ],
                  tag
                )
              )
                return "";
            }
          }),
          { length: 500 }
        )}
      />
    );
  };
  const sliderPanel = () => {
    return count <= 0 ? null : (
      <>
        <Flex mt={12} justifyContent="space-between">
          <Heading as="h2" fontSize="lg" fontWeight={700}>
            {translate("common.latestPosts")}
          </Heading>
          {count <= 1 ? null : (
            <Button
              size="xs"
              fontWeight="500"
              variant="tertiary"
              rightIcon={<FiArrowRight />}
              onClick={() =>
                window.open(
                  `/${projectId}/places/${permalink}`,
                  getRouteTarget()
                )
              }
            >
              {translate("common.seeAll")}
            </Button>
          )}
        </Flex>
        <Box
          style={{
            position: "relative",
            width: "100%",
            height: "400px"
          }}
          className="places-banner"
        >
          <Image
            alt={translate("common.poweredByEnagamentHQ")}
            src={addAssetPrefix("/images/worldmap.png")}
            width={"100%"}
            height={"100%"}
          />
          <Box
            style={{
              position: "absolute",
              bottom: "3%",
              left: 0,
              right: 0,
              display: "flex",
              overflow: "auto",
              gap: 1
            }}
            className="banner-box"
          >
            <HorizontalScrollMenu
              itemHeight="full"
              px={{ md: 3, sm: 3, base: 3 }}
              itemWidth={{
                md: 260,
                sm: count === 1 ? "full" : 212,
                base: count === 1 ? "full" : 212
              }}
              leftArrowPosition={1}
              rightArrowPosition={1}
              overlapContainerProps={{
                width: "92vw",
                left: "5px"
              }}
            >
              {markers.map((marker: any) => {
                const {
                  id,
                  comment,
                  fileUrl,
                  pinIcon,
                  pinColor,
                  pinName,
                  userImageUrl,
                  createdAt,
                  author,
                  voted,
                  totalVotes
                } = marker;

                const imageUrl = fileUrl?.includes("imgix.net")
                  ? fileUrl
                  : buildImgixUrl(fileUrl, 800, 800);

                const pinIconProps = {
                  pinColor: pinColor,
                  pinIcon: pinIcon,
                  pinId: id
                };

                return (
                  <Link
                    key={marker.id}
                    target={getRouteTarget()}
                    display="block"
                    textDecoration="none"
                    rel="noopener noreferrer"
                    sx={{ _hover: { textDecoration: "none" } }}
                    href={`/${projectId}/places/${permalink}?marker=${marker.id}`}
                  >
                    <Contribution
                      key={id}
                      cardVariant="elevated"
                      username={author}
                      contributionTime={dateWithSiteLocale(
                        createdAt,
                        language.toLowerCase()
                      )}
                      imageSrc={imageUrl}
                      imageAlt={pinName}
                      showImage={!!fileUrl}
                      cardDescription={comment ? stripAllHtml(comment) : ""}
                      userAvatarSrc={userImageUrl}
                      showFooter={true}
                      pinType={pinName || "Pin"}
                      isVoteDisabled={!isRegisteredUserLoggedIn}
                      tileBackgroundColor="gray.100"
                      isLoaded={!isEmpty(marker)}
                      pinColor={pinColor}
                      commentsCount={0}
                      isThumbsUp={true}
                      thumbsUpCount={totalVotes || 0}
                      shareText=""
                      hasBodyHighlight={false}
                      showMoreOptions={true}
                      showDetailedText
                      numberOfLines={imageUrl ? 3 : 6}
                      pinIcon={<PinIcon {...pinIconProps} />}
                      handleVoteUp={() => {}}
                      ariaLabelVoteIcon={`${translate("common.numberOfVotes", {
                        votesCount: totalVotes
                      })} ${
                        voted
                          ? translate("common.removeVote")
                          : translate("common.addVote")
                      }`}
                    />
                  </Link>
                );
              })}
            </HorizontalScrollMenu>
          </Box>
        </Box>
      </>
    );
  };

  const viewDetails = () => {
    return (
      <Button
        mt={12}
        display="block"
        size={{ base: "lg", md: "md" }}
        width={{ base: "full", md: "auto" }}
        onClick={() =>
          window.open(`/${projectId}/places/${permalink}`, getRouteTarget())
        }
      >
        {count <= 1
          ? translate("brainstormers.haveYourSay")
          : translate("common.seeAllPosts", { count: count })}
      </Button>
    );
  };
  return (
    <Box as="ul" listStyleType="none">
      <Box py={6} mx={{ md: 6, sm: 4, base: 4 }}>
        <Heading as="h1" fontSize="2xl" fontWeight={700}>
          <Link
            target={getRouteTarget()}
            color="brand.500"
            rel="noopener noreferrer"
            href={`/${projectId}/places/${placesId}`}
          >
            {name}
          </Link>
        </Heading>
        {wrapperComponent()}
        {statusMessages()}
        {sliderPanel()}
        {viewDetails()}
      </Box>
    </Box>
  );
};

export default PlacesBanner;
